import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Photo, Annotation } from '@/types';
import { photosApi } from '@/api';
import { ANNOTATION_COLORS } from '@/utils/constants';

type ToolType = Annotation['type'];

// DB stores integer coordinates. We use a fixed scale to convert fractions ↔ integers.
const COORD_SCALE = 10000;

interface PhotoAnnotatorProps {
  photo: Photo;
  onClose: () => void;
  onSave: (updatedPhoto: Photo) => void;
}

type NewAnnotation = Omit<Annotation, 'id'>;

const MIN_DRAG_DISTANCE = 5; // px — ignore accidental micro-drags

// ── Drawing helpers ─────────────────────────────────────────────

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number,
  color: string,
) {
  const headLen = 14;
  const angle = Math.atan2(y2 - y1, x2 - x1);

  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLen * Math.cos(angle - Math.PI / 6),
    y2 - headLen * Math.sin(angle - Math.PI / 6),
  );
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLen * Math.cos(angle + Math.PI / 6),
    y2 - headLen * Math.sin(angle + Math.PI / 6),
  );
  ctx.stroke();
}

function drawCircle(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, rx: number, ry: number,
  color: string,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.ellipse(cx, cy, Math.abs(rx) || 1, Math.abs(ry) || 1, 0, 0, 2 * Math.PI);
  ctx.stroke();
}

function drawRectangle(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  color: string,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.strokeRect(x, y, w, h);
}

function drawText(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, text: string,
  color: string,
) {
  const fontSize = 16;
  ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
  ctx.textBaseline = 'top';

  const metrics = ctx.measureText(text);
  const pad = 3;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(x - pad, y - pad, metrics.width + pad * 2, fontSize + pad * 2);

  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

function renderAnnotation(
  ctx: CanvasRenderingContext2D,
  a: NewAnnotation,
  cw: number, ch: number,
) {
  const px = a.x * cw;
  const py = a.y * ch;

  switch (a.type) {
    case 'arrow':
      drawArrow(ctx, px, py, px + (a.width ?? 0) * cw, py + (a.height ?? 0) * ch, a.color);
      break;
    case 'circle':
      drawCircle(ctx, px, py, (a.width ?? 0) * cw, (a.height ?? 0) * ch, a.color);
      break;
    case 'rectangle':
      drawRectangle(ctx, px, py, (a.width ?? 0) * cw, (a.height ?? 0) * ch, a.color);
      break;
    case 'text':
      if (a.text) drawText(ctx, px, py, a.text, a.color);
      break;
  }
}

// ── Component ───────────────────────────────────────────────────

const PhotoAnnotator: React.FC<PhotoAnnotatorProps> = ({ photo, onClose, onSave }) => {
  // Tool state
  const [activeTool, setActiveTool] = useState<ToolType>('arrow');
  const [activeColor, setActiveColor] = useState(ANNOTATION_COLORS[0]);

  // Drawing interaction
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentPreview, setCurrentPreview] = useState<NewAnnotation | null>(null);

  // Text tool
  const [pendingText, setPendingText] = useState<{ x: number; y: number } | null>(null);
  const [textInput, setTextInput] = useState('');

  // Annotation lists
  const [existingAnnotations, setExistingAnnotations] = useState<Annotation[]>([]);
  const [newAnnotations, setNewAnnotations] = useState<NewAnnotation[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Initialize existing annotations — convert DB integer coords to fractions
  useEffect(() => {
    const converted = (photo.annotations ?? []).map(a => ({
      ...a,
      x: a.x / COORD_SCALE,
      y: a.y / COORD_SCALE,
      width: a.width != null ? a.width / COORD_SCALE : undefined,
      height: a.height != null ? a.height / COORD_SCALE : undefined,
    }));
    setExistingAnnotations(converted);
  }, [photo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Canvas sizing ───────────────────────────────────────────

  // Ref to latest redraw function so syncCanvasSize can call it without circular deps
  const redrawRef = useRef<() => void>(() => {});

  const syncCanvasSize = useCallback(() => {
    const img = imageRef.current;
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!img || !container || !canvas) return;

    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const dpr = window.devicePixelRatio || 1;
    const w = imgRect.width;
    const h = imgRect.height;

    // Setting canvas.width/height clears all content and resets context state
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.left = `${imgRect.left - containerRect.left}px`;
    canvas.style.top = `${imgRect.top - containerRect.top}px`;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);

    // Canvas was cleared by dimension change, so redraw annotations
    redrawRef.current();
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    syncCanvasSize();
  }, [syncCanvasSize]);

  // ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      if (imageLoaded) syncCanvasSize();
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [imageLoaded, syncCanvasSize]);

  // ── Redraw ──────────────────────────────────────────────────

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cw = canvas.width / dpr;
    const ch = canvas.height / dpr;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw existing (minus deleted)
    for (const a of existingAnnotations) {
      if (!deletedIds.includes(a.id)) {
        renderAnnotation(ctx, a, cw, ch);
      }
    }

    // Draw new
    for (const a of newAnnotations) {
      renderAnnotation(ctx, a, cw, ch);
    }

    // Draw preview
    if (currentPreview) {
      renderAnnotation(ctx, currentPreview, cw, ch);
    }
  }, [existingAnnotations, newAnnotations, deletedIds, currentPreview]);

  // Keep ref in sync so syncCanvasSize always uses the latest redraw
  redrawRef.current = redrawCanvas;

  useEffect(() => {
    if (imageLoaded) redrawCanvas();
  }, [imageLoaded, redrawCanvas]);

  // ── Coordinate helpers ──────────────────────────────────────

  function getPctCoords(e: React.MouseEvent<HTMLCanvasElement> | React.Touch): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const clientX = 'clientX' in e ? e.clientX : (e as React.Touch).clientX;
    const clientY = 'clientY' in e ? e.clientY : (e as React.Touch).clientY;
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  }

  function pixelDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    return Math.hypot((b.x - a.x) * rect.width, (b.y - a.y) * rect.height);
  }

  // ── Build annotation from two points ────────────────────────

  function buildAnnotation(start: { x: number; y: number }, end: { x: number; y: number }): NewAnnotation {
    switch (activeTool) {
      case 'arrow':
        return { type: 'arrow', x: start.x, y: start.y, width: end.x - start.x, height: end.y - start.y, color: activeColor };
      case 'circle': {
        const cx = (start.x + end.x) / 2;
        const cy = (start.y + end.y) / 2;
        const rx = Math.abs(end.x - start.x) / 2;
        const ry = Math.abs(end.y - start.y) / 2;
        return { type: 'circle', x: cx, y: cy, width: rx, height: ry, color: activeColor };
      }
      case 'rectangle': {
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const w = Math.abs(end.x - start.x);
        const h = Math.abs(end.y - start.y);
        return { type: 'rectangle', x, y, width: w, height: h, color: activeColor };
      }
      default:
        return { type: 'arrow', x: start.x, y: start.y, width: 0, height: 0, color: activeColor };
    }
  }

  // ── Mouse handlers ──────────────────────────────────────────

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pt = getPctCoords(e);

    if (activeTool === 'text') {
      setPendingText(pt);
      setTextInput('');
      setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }

    setIsDrawing(true);
    setStartPoint(pt);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || activeTool === 'text') return;
    const pt = getPctCoords(e);
    setCurrentPreview(buildAnnotation(startPoint, pt));
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || activeTool === 'text') return;
    const pt = getPctCoords(e);

    if (pixelDistance(startPoint, pt) >= MIN_DRAG_DISTANCE) {
      setNewAnnotations(prev => [...prev, buildAnnotation(startPoint, pt)]);
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPreview(null);
  };

  const handleMouseLeave = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentPreview(null);
    }
  };

  // ── Touch handlers ──────────────────────────────────────────

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    const pt = getPctCoords(touch);

    if (activeTool === 'text') {
      setPendingText(pt);
      setTextInput('');
      setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }

    setIsDrawing(true);
    setStartPoint(pt);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || activeTool === 'text') return;
    e.preventDefault();
    const pt = getPctCoords(e.touches[0]);
    setCurrentPreview(buildAnnotation(startPoint, pt));
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint || activeTool === 'text') return;
    const pt = e.changedTouches.length > 0
      ? getPctCoords(e.changedTouches[0])
      : startPoint;

    if (pixelDistance(startPoint, pt) >= MIN_DRAG_DISTANCE) {
      setNewAnnotations(prev => [...prev, buildAnnotation(startPoint, pt)]);
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPreview(null);
  };

  // ── Text commit ─────────────────────────────────────────────

  const commitText = () => {
    if (pendingText && textInput.trim()) {
      setNewAnnotations(prev => [
        ...prev,
        { type: 'text' as const, x: pendingText.x, y: pendingText.y, text: textInput.trim(), color: activeColor },
      ]);
    }
    setPendingText(null);
    setTextInput('');
  };

  // ── Delete existing annotation ──────────────────────────────

  const handleDeleteAnnotation = (id: string) => {
    setDeletedIds(prev => [...prev, id]);
  };

  // ── Undo ────────────────────────────────────────────────────

  const handleUndo = () => {
    setNewAnnotations(prev => prev.slice(0, -1));
  };

  // ── Save ────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Delete removed existing annotations
      await Promise.all(
        deletedIds.map(id => photosApi.deleteAnnotation(photo.id, id))
      );

      // POST new annotations — convert fractions to DB integers
      const saved = await Promise.all(
        newAnnotations.map(a => photosApi.addAnnotation(photo.id, {
          ...a,
          x: Math.round(a.x * COORD_SCALE),
          y: Math.round(a.y * COORD_SCALE),
          width: a.width != null ? Math.round(a.width * COORD_SCALE) : undefined,
          height: a.height != null ? Math.round(a.height * COORD_SCALE) : undefined,
        }))
      );

      // API returns DB integers — convert back to fractions for local state
      const savedData: Annotation[] = saved
        .filter(r => r.success && r.data)
        .map(r => {
          const d = r.data!;
          return {
            ...d,
            x: d.x / COORD_SCALE,
            y: d.y / COORD_SCALE,
            width: d.width != null ? d.width / COORD_SCALE : undefined,
            height: d.height != null ? d.height / COORD_SCALE : undefined,
          };
        });

      const remaining = existingAnnotations.filter(a => !deletedIds.includes(a.id));
      const updatedPhoto: Photo = {
        ...photo,
        annotations: [...remaining, ...savedData],
      };

      onSave(updatedPhoto);
      onClose();
    } catch {
      setSaveError('Failed to save annotations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = newAnnotations.length > 0 || deletedIds.length > 0;

  // ── Tool config ─────────────────────────────────────────────

  const tools: { type: ToolType; label: string; icon: React.ReactNode }[] = [
    {
      type: 'arrow',
      label: 'Arrow',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H8M17 7v9" />
        </svg>
      ),
    },
    {
      type: 'circle',
      label: 'Circle',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" strokeWidth={2} />
        </svg>
      ),
    },
    {
      type: 'rectangle',
      label: 'Rect',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="14" rx="1" strokeWidth={2} />
        </svg>
      ),
    },
    {
      type: 'text',
      label: 'Text',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 4v3h5.5v12h3V7H19V4H5z" />
        </svg>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl flex flex-col w-full max-w-4xl max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Annotate Photo</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Canvas area */}
        <div
          ref={containerRef}
          className="relative flex-1 overflow-hidden bg-gray-900 flex items-center justify-center min-h-0"
        >
          <img
            ref={imageRef}
            src={photo.url}
            alt="Annotate"
            className="max-w-full max-h-full object-contain"
            onLoad={handleImageLoad}
            draggable={false}
          />
          <canvas
            ref={canvasRef}
            className="absolute"
            style={{ cursor: activeTool === 'text' ? 'text' : 'crosshair' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          {/* Text input overlay */}
          {pendingText && (
            <input
              ref={textInputRef}
              type="text"
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') commitText();
                if (e.key === 'Escape') { setPendingText(null); setTextInput(''); }
              }}
              onBlur={commitText}
              autoFocus
              className="absolute z-10 outline-none min-w-[80px]"
              style={{
                left: `${pendingText.x * 100}%`,
                top: `${pendingText.y * 100}%`,
                background: 'rgba(0,0,0,0.4)',
                border: 'none',
                borderBottom: `2px solid ${activeColor}`,
                color: activeColor,
                font: 'bold 16px system-ui, sans-serif',
                padding: '2px 4px',
              }}
              placeholder="Type here..."
            />
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-200 flex-shrink-0 flex-wrap">
          {/* Tools */}
          <div className="flex items-center gap-1">
            {tools.map(tool => (
              <button
                key={tool.type}
                onClick={() => setActiveTool(tool.type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  activeTool === tool.type
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tool.icon}
                {tool.label}
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Colors */}
          <div className="flex items-center gap-1">
            {ANNOTATION_COLORS.map(color => (
              <button
                key={color}
                onClick={() => setActiveColor(color)}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  activeColor === color
                    ? 'border-white ring-2 ring-primary-500 scale-110'
                    : 'border-gray-300 hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          {/* Separator */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Existing annotation delete list */}
          {existingAnnotations.filter(a => !deletedIds.includes(a.id)).length > 0 && (
            <>
              <div className="flex items-center gap-1">
                {existingAnnotations.filter(a => !deletedIds.includes(a.id)).map((a, i) => (
                  <button
                    key={a.id}
                    onClick={() => handleDeleteAnnotation(a.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-red-50 hover:text-red-600 transition-colors"
                    title={`Delete ${a.type} #${i + 1}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: a.color }} />
                    {a.type}
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                ))}
              </div>
              <div className="w-px h-6 bg-gray-300 mx-1" />
            </>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Undo */}
          <button
            onClick={handleUndo}
            disabled={newAnnotations.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" />
            </svg>
            Undo
          </button>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Error */}
        {saveError && (
          <div className="px-4 pb-3 text-sm text-red-600">{saveError}</div>
        )}
      </div>
    </div>
  );
};

export default PhotoAnnotator;
